import React, { useState, useEffect, useCallback } from "react";
import { Truck, Plus, Edit2, Trash2, Save, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

const MEXICAN_STATES = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas",
  "Chihuahua", "Ciudad de México", "Coahuila", "Colima", "Durango", "Guanajuato",
  "Guerrero", "Hidalgo", "Jalisco", "Estado de México", "Michoacán", "Morelos",
  "Nayarit", "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo",
  "San Luis Potosí", "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala",
  "Veracruz", "Yucatán", "Zacatecas"
];

const emptyZone = {
  name: "",
  states: [],
  base_rate: 0,
  per_kg_rate: 0,
  min_weight: 0,
  max_weight: 100,
  free_shipping_threshold: null,
  estimated_days: "3-5 días",
  active: true,
};

export default function Envios() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/shipping/zones");
      setZones(res.data);
    } catch {
      toast.error("Error al cargar zonas de envío");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing({ ...emptyZone });
    setShowForm(true);
  };

  const openEdit = (zone) => {
    setEditing({ ...zone });
    setShowForm(true);
  };

  const closeForm = () => {
    setEditing(null);
    setShowForm(false);
  };

  const set = (key, value) => {
    setEditing(e => ({ ...e, [key]: value }));
  };

  const toggleState = (state) => {
    setEditing(e => {
      const current = e.states || [];
      if (current.includes(state)) {
        return { ...e, states: current.filter(s => s !== state) };
      } else {
        return { ...e, states: [...current, state] };
      }
    });
  };

  const save = async () => {
    if (!editing.name) { toast.error("El nombre es requerido"); return; }
    if (!editing.states.length) { toast.error("Selecciona al menos un estado"); return; }
    
    setSaving(true);
    try {
      const data = {
        ...editing,
        free_shipping_threshold: editing.free_shipping_threshold || null,
      };
      
      if (editing.id) {
        await api.put(`/shipping/zones/${editing.id}`, data);
        toast.success("Zona actualizada");
      } else {
        await api.post("/shipping/zones", data);
        toast.success("Zona creada");
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deleteZone = async (zone) => {
    if (!window.confirm(`¿Eliminar la zona "${zone.name}"?`)) return;
    try {
      await api.delete(`/shipping/zones/${zone.id}`);
      toast.success("Zona eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Tarifas de Envío</h1>
          <p className="text-slate-500 text-sm mt-0.5">Configura las zonas y tarifas para el portal de clientes</p>
        </div>
        <button onClick={openNew} data-testid="new-zone-btn"
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors">
          <Plus size={16} /> Nueva zona
        </button>
      </div>

      {/* Info box */}
      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800">
        <div className="font-semibold mb-1 flex items-center gap-2">
          <Truck size={16} /> ¿Cómo funciona?
        </div>
        <ul className="text-brand-600 text-xs space-y-1 ml-6 list-disc">
          <li>Crea zonas agrupando estados de México</li>
          <li>Define tarifa base + costo por kg adicional</li>
          <li>Opcionalmente, configura un monto mínimo para envío gratis</li>
          <li>Los clientes del portal verán el costo de envío calculado automáticamente</li>
        </ul>
      </div>

      {/* Zones list */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : zones.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <MapPin size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Sin zonas configuradas</p>
          <p className="text-slate-400 text-sm mt-1">Crea tu primera zona de envío</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {zones.map(zone => (
            <div key={zone.id} className={`bg-white rounded-xl border ${zone.active ? "border-slate-200" : "border-slate-100 opacity-60"} p-4 shadow-sm`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900">{zone.name}</span>
                    {!zone.active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Inactiva</span>}
                    {zone.free_shipping_threshold && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        Envío gratis desde {formatMXN(zone.free_shipping_threshold)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(zone.states || []).slice(0, 6).map(s => (
                      <span key={s} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                    {(zone.states || []).length > 6 && (
                      <span className="text-xs text-slate-400">+{zone.states.length - 6} más</span>
                    )}
                  </div>
                  <div className="flex gap-4 mt-2 text-sm text-slate-600">
                    <span>Base: <b>{formatMXN(zone.base_rate)}</b></span>
                    <span>Por kg: <b>{formatMXN(zone.per_kg_rate)}</b></span>
                    <span>⏱️ {zone.estimated_days}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(zone)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-brand-600 transition-colors">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => deleteZone(zone)} className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-brand-600 px-5 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="text-white font-bold">{editing.id ? "Editar zona" : "Nueva zona de envío"}</div>
              <button onClick={closeForm} className="text-white/70 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de la zona *</label>
                <input value={editing.name} onChange={e => set("name", e.target.value)}
                  placeholder="Ej: Zona Norte, Local, Nacional..."
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* States */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estados incluidos *</label>
                <div className="bg-slate-50 rounded-xl p-3 max-h-48 overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5">
                    {MEXICAN_STATES.map(state => (
                      <button key={state} type="button" onClick={() => toggleState(state)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                          (editing.states || []).includes(state)
                            ? "bg-brand-600 text-white"
                            : "bg-white border border-slate-200 text-slate-600 hover:border-brand-300"
                        }`}>
                        {state}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => set("states", [...MEXICAN_STATES])}
                    className="text-xs text-brand-600 hover:underline">Seleccionar todos</button>
                  <button type="button" onClick={() => set("states", [])}
                    className="text-xs text-slate-400 hover:underline">Limpiar</button>
                </div>
              </div>

              {/* Rates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tarifa base ($)</label>
                  <input type="number" value={editing.base_rate} onChange={e => set("base_rate", parseFloat(e.target.value) || 0)}
                    placeholder="0.00" min="0" step="0.01"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Por kg adicional ($)</label>
                  <input type="number" value={editing.per_kg_rate} onChange={e => set("per_kg_rate", parseFloat(e.target.value) || 0)}
                    placeholder="0.00" min="0" step="0.01"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>

              {/* Free shipping threshold */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Envío gratis a partir de ($)</label>
                <input type="number" value={editing.free_shipping_threshold || ""} 
                  onChange={e => set("free_shipping_threshold", e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="Ej: 500 (dejar vacío si no aplica)" min="0" step="0.01"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                <p className="text-xs text-slate-400 mt-1">Deja vacío si no ofreces envío gratis</p>
              </div>

              {/* Estimated days */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tiempo estimado de entrega</label>
                <input value={editing.estimated_days} onChange={e => set("estimated_days", e.target.value)}
                  placeholder="Ej: 3-5 días hábiles"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* Active */}
              <div className="flex items-center gap-3 pt-2">
                <input type="checkbox" id="active" checked={editing.active} onChange={e => set("active", e.target.checked)}
                  className="w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500" />
                <label htmlFor="active" className="text-sm text-slate-700">Zona activa (visible en el portal)</label>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3">
                <button onClick={closeForm}
                  className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 py-2.5 rounded-xl text-sm font-medium transition-colors">
                  Cancelar
                </button>
                <button onClick={save} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                  {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
